import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import Card from '../../components/UI/Card';
import PageHeader from '../../components/UI/PageHeader';
import Button from '../../components/UI/Button';
import FormInput from '../../components/Forms/FormInput';
import FormSelect from '../../components/Forms/FormSelect';
import { getApplicationById, updateApplication } from '../../services/applications/applicationsService';
import { ApplicationDto, ApplicationStatus, ApplicationVendor } from '../../services/applications/types';

const ApplicationEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auth = useAuth();
  const [formData, setFormData] = useState<ApplicationDto>({
    name: '',
    identifier: '',
    status: ApplicationStatus.ACTIVATED,
    vendor: ApplicationVendor.WEB
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    if (id) {
      fetchApplication(id);
    }
  }, [id, auth.user]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const fetchApplication = async (applicationId: string) => {
    if (!auth.isAuthenticated) {
      setError('You must be authenticated to edit an application.');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await getApplicationById(auth.user || null, applicationId);
      setFormData({
        ...data,
        // Ensure required fields are set (even if API doesn't return them)
        status: data.status || ApplicationStatus.ACTIVATED,
        vendor: data.vendor || ApplicationVendor.WEB
      });
    } catch (err) {
      setError('Failed to fetch application data. Please try again.');
      console.error('Error fetching application:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!auth.isAuthenticated) {
      setError('You must be authenticated to update an application.');
      return;
    }
    
    if (!id) {
      setError('Application ID is missing.');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const updatedApplication = await updateApplication(auth.user || null, id, formData);
      navigate(`/applications/${updatedApplication.id}`);
    } catch (err) {
      setError('Failed to update application. Please try again.');
      console.error('Error updating application:', err);
      setIsSubmitting(false);
    }
  };
  
  const handleCancel = () => {
    navigate(`/applications/${id}`);
  };
  
  return (
    <div>
      <PageHeader
        title={isLoading ? 'Loading...' : 'Edit Application'}
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Applications", to: "/applications" },
          { label: formData.name || 'Application', to: `/applications/${id}` },
          { label: isLoading ? 'Loading...' : 'Edit' }
        ]}
      />
      
      <Card>
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 text-red-700 p-4 rounded-md mb-4">
            {error}
            <Button 
              variant="ghost" 
              size="sm" 
              className="ml-2"
              onClick={() => id && fetchApplication(id)}
            >
              Retry
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                <div className="space-y-4">
                  <FormInput
                    label="Name"
                    id="name"
                    name="name"
                    required
                    value={formData.name || ''}
                    onChange={handleInputChange}
                    placeholder="Enter application name"
                  />
                  
                  <FormInput
                    label="Identifier"
                    id="identifier"
                    name="identifier"
                    value={formData.identifier || ''}
                    disabled
                    readOnly
                    placeholder="Identifier cannot be changed"
                    helperText="Identifier cannot be changed after creation."
                  />
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Configuration</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormSelect
                    label="Vendor"
                    id="vendor"
                    name="vendor"
                    required
                    value={formData.vendor || ''}
                    onChange={handleInputChange}
                    options={Object.entries(ApplicationVendor).map(([key, value]) => ({
                      value: value,
                      label: key
                    }))}
                  />
                  
                  <FormSelect
                    label="Status"
                    id="status"
                    name="status"
                    required
                    value={formData.status || ''}
                    onChange={handleInputChange}
                    options={Object.entries(ApplicationStatus).map(([key, value]) => ({
                      value: value,
                      label: key
                    }))}
                  />
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-2">
              <Button
                type="button"
                variant="secondary"
                onClick={handleCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Update Application'}
              </Button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
};

export default ApplicationEdit;